import { Table, Dropdown } from "@/config/antd";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import { Link } from "react-router-dom";
import Avatar from "@/components/common/Avatar";
import { useState } from "react";
import Delete from "@/components/common/alerts/Delete";
import WebIcon from "@/components/common/Web_Icons";
import { useSelector } from "react-redux";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";

const FinancialHomeTable = ({
  transactions,
  setSelectedTransactions,
  isLoading,
  onDelete,
  onModify,
}) => {
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  // Return null if there's no data or loading
  if (!transactions || transactions.length === 0 || isLoading) {
    return null;
  }

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedTransactions(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // Transform the transactions data to match table structure
  const tableData = transactions.map((transaction) => ({
    key: transaction?._id,
    transaction_type: transaction?.name,
    id: transaction?._id,
    amount: `₦${transaction?.amount || transaction?.grandTotal}`,
    initiator: transaction?.merchantInformation?.name,
    // Leave empty so Avatar falls back to the global dummy profile image
    imgUrl: transaction?.merchantInformation?.profileImage || null,
    category: transaction?.itemCategoryID,
    date: transaction?.createdAt
      ? new Date(transaction.createdAt).toLocaleString()
      : "N/A",
    status: "Pending",
  }));

  // Apply global search filter
  const filteredData = globalSearchTerm
    ? tableData.filter(
        (item) =>
          item.transaction_type
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.amount?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.initiator
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.category
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      )
    : tableData;

  const handleMenuClick = (menuItem, record) => {
    if (menuItem.key === "delete") {
      setSelectedTransaction(record);
      setDeleteModalVisible(true);
    } else if (menuItem.key === "modify") {
      onModify(record.id);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedTransaction) {
      onDelete(selectedTransaction.id, selectedTransaction.transaction_type);
      setDeleteModalVisible(false);
      setSelectedTransaction(null);
    }
  };

  return (
    <>
      <Table
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={filteredData}
        loading={isLoading}
        rowKey="id">
        <>
          <Column
            title="Transaction Type"
            dataIndex="transaction_type"
            key="transaction_type"
            render={(_, { id, transaction_type }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="flex items-center gap-3 font-bold text-[15px]">
                <div className="bg-secondary w-10 h-10 rounded-full flex items-center justify-center">
                  <WebIcon icon="Watch" size="large" />
                </div>
                <p className="text-regular font-bold truncate w-[200px]">
                  {transaction_type}
                </p>
              </Link>
            )}
          />
          <Column
            title="Amount"
            dataIndex="amount"
            key="amount"
            render={(_, { id, amount }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="font-bold text-[15px]">
                <p>{amount}</p>
              </Link>
            )}
          />
          <Column
            title="Initiator"
            dataIndex="initiator"
            key="initiator"
            render={(_, { id, initiator, imgUrl }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="flex items-center gap-5 text-sm font-bold">
                <Avatar src={imgUrl} alt={initiator || "Initiator"} size={40} />
                <p className="text-regular font-bold">{initiator}</p>
              </Link>
            )}
          />
          <Column
            title="Category"
            dataIndex="category"
            key="category"
            render={(_, { id, category }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="text-[15px] font-bold">
                <p className="text-regular font-bold">{category}</p>
              </Link>
            )}
          />
          <Column
            title="Date and Time"
            key="date"
            dataIndex="date"
            render={(_, { id, date }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="text-xs">
                <p className="text-small whitespace-nowrap">{date}</p>
              </Link>
            )}
          />
          <Column
            title="Status"
            key="status"
            dataIndex="status"
            render={(_, { id, status }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <p className="text-primary font-bold text-small">{status}</p>
              </Link>
            )}
          />
          <Column
            title=""
            dataIndex="options"
            key="options"
            render={(_, record) => (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "modify",
                      label: "Modify",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleMenuClick({ key: "modify" }, record);
                      },
                    },
                    {
                      key: "delete",
                      label: "Delete",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleMenuClick({ key: "delete" }, record);
                      },
                    },
                  ],
                }}
                trigger={["click"]}>
                <CustomMoreOptions
                  variant="filled"
                  onClick={(e) => e.stopPropagation()}
                  color="black"
                  orientation="vertical"
                  size="small"
                />
              </Dropdown>
            )}
          />
        </>
      </Table>

      <Delete
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Transaction?"
        paragraph={
          selectedTransaction
            ? `Are you sure you want to delete the transaction "${selectedTransaction.transaction_type}" from your existing list?`
            : ""
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

FinancialHomeTable.propTypes = {
  transactions: PropTypes.array,
  setSelectedTransactions: PropTypes.func,
  isLoading: PropTypes.bool,
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
};

export default FinancialHomeTable;
